import React, { useState, useEffect, useMemo } from 'react';
import { MapPin, Clock, Star, ShoppingCart, Package, Users, X, Loader, Plus, Minus, TriangleAlert as AlertTriangle, Info, Navigation, Zap } from 'lucide-react';
import { trackError } from '../Utils/analytics';
import { authenticatedFetch, FOOD_FALLBACK_IMG } from '../Utils/apiHelpers';
import { FoodItem, useOnlineKitchensWithFood } from '../Utils/useOnlineKitchensWithFood';
import { formatFoodCategory } from '../Utils/category';
import { ORDER_HISTORY_URL } from '../../config/api';
import { useCart } from '../Utils/useCart';
import FoodDetailModal from '../FoodDetailModal/FoodDetailModal';
import '../FeaturedMeals/FeaturedMeals.css';

interface FeaturedMealsProps {
  filteredKitchenId?: number | null;
  filteredKitchenName?: string | null;
  onClearFilter?: () => void;
  onCartChange?: () => void;
  cartRefreshToken?: number;
  locationVersion?: number;
  showNotification?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

interface ReplaceCartConfirm {
  foodId: number;
  foodName: string;
}

const FeaturedMeals: React.FC<FeaturedMealsProps> = ({
  filteredKitchenId = null,
  filteredKitchenName = null,
  onClearFilter,
  onCartChange,
  cartRefreshToken = 0,
  locationVersion = 0,
  showNotification,
}) => {
  const { meals, isLoading, error } = useOnlineKitchensWithFood(locationVersion);
  const [replaceConfirm, setReplaceConfirm] = useState<ReplaceCartConfirm | null>(null);
  const [detailTarget, setDetailTarget] = useState<{ foodId: number; foodName: string } | null>(null);
  const [orderedFoodIds, setOrderedFoodIds] = useState<Set<number>>(new Set());
  const isLoggedIn = !!localStorage.getItem('authToken');

  const { cartMap, handleIncrement, handleDecrement, replaceCartItem } = useCart<FoodItem>({
    cartRefreshToken,
    onCartChange,
    isAuthenticated: isLoggedIn,
    analyticsEventName: 'food_added_to_cart',
    getId: meal => meal.id,
    getName: meal => meal.food_name,
    onCartConflict: (foodId, foodName) => setReplaceConfirm({ foodId, foodName }),
    onAddFailed: () => trackError('cart_add_failed', 'featured_meals'),
  });

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchOrderedFoods = async () => {
      try {
        const res = await authenticatedFetch(ORDER_HISTORY_URL(1, 100));
        if (!res.ok) return;
        const json = await res.json();
        const ids = new Set<number>();
        (json.orders || []).forEach((order: any) => {
          const status = ((order.order_status || order.status || '') + '').toLowerCase().trim();
          if (status !== 'delivered' && status !== 'completed') return;
          (order.items || []).forEach((item: any) => {
            if (item.food_id) ids.add(item.food_id);
          });
        });
        setOrderedFoodIds(ids);
      } catch {
        // non-critical
      }
    };
    fetchOrderedFoods();
  }, [isLoggedIn]);

  const handleConfirmReplace = async () => {
    if (!replaceConfirm) return;
    const { foodId, foodName } = replaceConfirm;
    setReplaceConfirm(null);
    await replaceCartItem(foodId, foodName);
  };

  const visibleMeals = useMemo(() => {
    if (!filteredKitchenId) return meals;
    return meals.filter(m => m.kitchen_id === filteredKitchenId);
  }, [meals, filteredKitchenId]);

  const sectionTitle = filteredKitchenName
    ? `Food from ${filteredKitchenName}`
    : 'Hot & Fresh Near You';

  return (
    <section id="featured-meals" className="featured-meals">
      <div className="meals-header">
        <h2 className="featured-meals-title">{sectionTitle}</h2>
        <p className="meals-subtitle">Homemade meals from kitchens within 15 km, ready for delivery</p>

        {filteredKitchenId && filteredKitchenName && onClearFilter && (
          <div className="meals-filter-strip">
            <span>Filtering by kitchen: <strong>{filteredKitchenName}</strong></span>
            <button className="meals-filter-clear" onClick={onClearFilter}>
              <X size={14} />
              Show all kitchens
            </button>
          </div>
        )}

        {isLoading && (
          <div className="meals-loading">
            <div className="loading-spinner"></div>
            <p>Loading meals...</p>
          </div>
        )}
        {error && (
          <div className="meals-error">
            <p>{error}</p>
          </div>
        )}
      </div>

      {/* Replace-cart confirmation dialog */}
      {replaceConfirm && (
        <div className="cart-replace-overlay" onClick={() => setReplaceConfirm(null)}>
          <div className="cart-replace-dialog" onClick={e => e.stopPropagation()}>
            <div className="cart-replace-icon">
              <AlertTriangle size={28} color="#ff6b35" />
            </div>
            <h3>Replace Cart?</h3>
            <p>
              Your cart has items from a different kitchen. Adding <strong>{replaceConfirm.foodName}</strong> will
              clear your current cart and start a new one from this kitchen.
            </p>
            <div className="cart-replace-actions">
              <button className="cart-replace-cancel" onClick={() => setReplaceConfirm(null)}>
                Keep Current Cart
              </button>
              <button className="cart-replace-confirm" onClick={handleConfirmReplace}>
                Replace &amp; Add
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <div className="meals-grid">
          {visibleMeals.length === 0 ? (
            <div className="no-meals-message">
              <p>
                {filteredKitchenId
                  ? 'No food items found for this kitchen. Try a different one!'
                  : 'No meals available at the moment. Check back soon!'}
              </p>
            </div>
          ) : (
            visibleMeals.map(meal => {
              const imageUrl = meal.image_url || FOOD_FALLBACK_IMG;
              const cartItem = cartMap[meal.id];
              const qty = cartItem?.quantity ?? 0;
              const loading = cartItem?.loading ?? false;

              return (
                <div key={meal.id} className="meal-card">
                  <div className="meal-image-container">
                    <img
                      src={imageUrl}
                      alt={meal.food_name}
                      className="meal-image"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = FOOD_FALLBACK_IMG;
                      }}
                    />
                    {meal.category && (
                      <span className={`meal-category-badge meal-category-badge--${meal.category.toLowerCase().replace(/\s+/g, '-')}`}>
                        {formatFoodCategory(meal.category)}
                      </span>
                    )}
                    <div className="meal-badges">
                      {meal.average_rating !== undefined && meal.average_rating > 0 && (
                        <span className="rating-badge rating-badge--stars">
                          <Star size={11} fill="currentColor" />
                          {meal.average_rating.toFixed(1)}
                          {meal.review_count !== undefined && meal.review_count > 0 && (
                            <span className="rating-badge-count">({meal.review_count})</span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="meal-content">
                    <div className="meal-header">
                      <h3 className="meal-title">{meal.food_name}</h3>
                      <p className="meal-chef">by {meal.kitchen_name}</p>
                    </div>

                    {meal.description && meal.description !== 'string' && (
                      <p className="meal-desc">{meal.description.length > 70 ? `${meal.description.substring(0, 70)}...` : meal.description}</p>
                    )}

                    <div className="meal-card-badges">
                      {meal.badges?.map(b => (
                        <span key={b} className={`meal-badge meal-badge--${b.toLowerCase().replace(/\s+/g, '-')}`}>{b}</span>
                      ))}
                      {meal.stock_status && meal.stock_status !== 'In Stock' && (
                        <span className="meal-badge meal-badge--stock">{meal.stock_status}</span>
                      )}
                      {meal.delivery_status && (
                        <span className="meal-badge meal-badge--delivery"><Zap size={10} /> {meal.delivery_status}</span>
                      )}
                    </div>

                    <div className="meal-meta">
                      <div className="meta-item">
                        <MapPin size={13} />
                        <span>
                          {meal.pickup_location && meal.pickup_location !== 'string'
                            ? (meal.pickup_location.length > 20 ? `${meal.pickup_location.substring(0, 20)}...` : meal.pickup_location)
                            : 'Pickup available'}
                        </span>
                      </div>
                      <div className="meta-item">
                        <Clock size={13} />
                        <span>{meal.preparation_time || 'Ready soon'}</span>
                      </div>
                      <div className="meta-item">
                        <Package size={13} />
                        <span>Qty: {meal.available_quantity}</span>
                      </div>
                      <div className="meta-item">
                        <Users size={13} />
                        <span>Serves: {meal.serving_size || '1'}</span>
                      </div>
                      {meal.distance_km !== undefined && meal.distance_km !== null && (
                        <div className="meta-item">
                          <Navigation size={13} />
                          <span>{meal.distance_km.toFixed(1)} km</span>
                        </div>
                      )}
                      {meal.eta && (
                        <div className="meta-item">
                          <Clock size={13} />
                          <span>{meal.eta}</span>
                        </div>
                      )}
                    </div>

                    <div className="meal-pricing">
                      <div className="price-info">
                        <span className="meal-price">
                          £{typeof meal.price === 'number' ? meal.price.toFixed(2) : meal.price}
                        </span>
                      </div>
                      <div className="meal-actions">
                        <button
                          className="meal-info-btn"
                          onClick={() => setDetailTarget({ foodId: meal.id, foodName: meal.food_name })}
                          title="View details & reviews"
                        >
                          <Info size={14} />
                        </button>
                        {qty > 0 ? (
                          <div className="meal-qty-controls">
                            <button
                              className="meal-qty-btn"
                              onClick={() => handleDecrement(meal)}
                              disabled={loading}
                            >
                              <Minus size={14} />
                            </button>
                            <span className="meal-qty-value">
                              {loading ? <Loader size={14} className="meal-btn-spinner" /> : qty}
                            </span>
                            <button
                              className="meal-qty-btn"
                              onClick={() => handleIncrement(meal)}
                              disabled={loading}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleIncrement(meal)}
                            className="order-btn"
                            disabled={loading}
                          >
                            {loading ? (
                              <Loader size={16} className="meal-btn-spinner" />
                            ) : (
                              <ShoppingCart size={16} />
                            )}
                            {loading ? 'Adding...' : 'Add to Cart'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <div className="delivery-info">
        <div className="info-card">
          <h4>🚚 Fast Delivery</h4>
          <p>Average delivery time: 25-35 minutes</p>
        </div>
        <div className="info-card">
          <h4>👨‍🍳 Online Kitchens</h4>
          <p>Fresh meals from active kitchens in your area</p>
        </div>
        <div className="info-card">
          <h4>📱 Easy Ordering</h4>
          <p>Simple ordering process with secure payments</p>
        </div>
      </div>

      {detailTarget && (
        <FoodDetailModal
          foodId={detailTarget.foodId}
          onClose={() => setDetailTarget(null)}
          showNotification={showNotification}
          onCartChange={onCartChange}
          canReview={isLoggedIn && orderedFoodIds.has(detailTarget.foodId)}
        />
      )}
    </section>
  );
};

export default FeaturedMeals;
